// A caixa do canto: UMA caixa, quatro momentos. Quem avisa é quem atualiza.
//
// (Douglas 22/09/2026) "o aviso sutil em tela avisando que o crm receberá uma
// atualização em 5 4 3 2 1 ... e depois no final aparece o botão de atualizar
// como é hoje. este botão pode ser o mesmo que avisa, é o mesmo que atualiza."
//
//   1. contagem     — o deploy avisou que vai reiniciar; o número desce sozinho
//   2. atualizando  — o backend está fora (9-13s). Não é erro, e nada se perdeu
//   3. voltou       — o backend respondeu. Desfecho, então verde, e sai em 4s
//   4. nova versao  — o front novo subiu: o botao "Atualizar agora" de sempre
//
// A contagem só existe porque o deploy AVISA antes (arquivo deploy-aviso.json,
// servido pelo nginx — nunca pelo backend, que é justamente quem cai). Backend
// que morre sozinho, sem deploy, não tem contagem: a caixa aparece no momento 4,
// como era antes.
//
// ⚠️ Estilo em token, nunca em hex. A versão anterior tinha `background: white`
// fixo (caixa branca no tema escuro), 5 cores em hex, font-weight 600 e um emoji
// — quatro coisas que a lei visual do CRM não admite.

const ORIGEM_API = String(
  import.meta.env.VITE_SUPABASE_URL ?? 'https://beta.crm-api.primetecia.com.br/api',
).replace(/\/api\/?$/, '');

type Fase = 'contagem' | 'atualizando' | 'voltou' | 'nova_versao';

let caixa: HTMLDivElement | null = null;
let fase: Fase | null = null;
let versaoDisponivel: string | undefined;
let linkAtualizar: HTMLAnchorElement | null = null;
let tique: ReturnType<typeof setInterval> | null = null;
let reinicioEm = 0;
let expiraEm = 0;
let dispensadoAte = 0;

function urlParaAtualizar() {
  const url = new URL(window.location.href);
  url.searchParams.set('_v', versaoDisponivel || `r${Date.now()}`);
  return url.toString();
}

function fechar() {
  if (tique) { clearInterval(tique); tique = null; }
  caixa?.remove();
  caixa = null;
  fase = null;
  linkAtualizar = null;
}

function garantirCaixa() {
  if (caixa) return caixa;
  const div = document.createElement('div');
  // Filete bordô à esquerda com o raio do CRM, e o padding da esquerda
  // compensando os 3px — é o mesmo tratamento do card do Pedido 360.
  div.style.cssText = [
    'position: fixed', 'bottom: 24px', 'right: 24px', 'z-index: 2147483647',
    'pointer-events: auto',
    'background: hsl(var(--card))', 'color: hsl(var(--card-foreground))',
    'border: 1px solid hsl(var(--border))',
    'border-left: 3px solid hsl(var(--primary))',
    'border-radius: var(--radius)',
    'padding: 12px 16px 12px 19px',
    'box-shadow: 0 8px 24px rgb(0 0 0 / 0.12)',
    'max-width: 340px', 'font-family: inherit', 'font-size: 13px', 'line-height: 1.45',
  ].join(';');
  // Modal aberto (Radix) põe pointer-events:none no body e trata clique fora como
  // "fechar o modal", engolindo o clique. Captura antes e não deixa propagar.
  div.addEventListener('pointerdown', (e) => e.stopPropagation(), true);
  document.body.appendChild(div);
  caixa = div;
  return div;
}

function pintar(titulo: string, corpo: string, comBotoes: boolean, corTitulo = 'hsl(var(--primary))') {
  const div = garantirCaixa();
  div.innerHTML = `
    <p style="margin:0 0 4px;font-weight:500;font-size:13px;color:${corTitulo};">${titulo}</p>
    <p style="margin:0;color:hsl(var(--muted-foreground));">${corpo}</p>
    ${comBotoes ? `
    <div style="display:flex;gap:8px;margin-top:10px;">
      <a id="prime-reload-btn" href="${urlParaAtualizar()}" style="display:inline-flex;align-items:center;background:hsl(var(--primary));color:hsl(var(--primary-foreground));border:1px solid hsl(var(--primary));padding:6px 12px;border-radius:calc(var(--radius) - 4px);font-size:13px;font-weight:500;cursor:pointer;text-decoration:none;">Atualizar agora</a>
      <button id="prime-dismiss-btn" style="background:transparent;color:hsl(var(--muted-foreground));border:1px solid hsl(var(--border));padding:6px 12px;border-radius:calc(var(--radius) - 4px);font-size:13px;font-family:inherit;cursor:pointer;">Depois</button>
    </div>` : ''}
  `;
  if (!comBotoes) { linkAtualizar = null; return; }
  // Link nativo de propósito: continua funcionando mesmo se algum listener da
  // tela antiga tiver morrido — o botão de antes dependia só do listener.
  linkAtualizar = div.querySelector<HTMLAnchorElement>('#prime-reload-btn');
  const atualizar = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.assign(linkAtualizar?.href || urlParaAtualizar());
  };
  linkAtualizar?.addEventListener('pointerdown', atualizar, true);
  linkAtualizar?.addEventListener('click', atualizar, true);
  div.querySelector('#prime-dismiss-btn')?.addEventListener('click', () => {
    fechar();
    // Deixa o aviso voltar depois — inclusive se outro chunk falhar.
    dispensadoAte = Date.now() + 30000;
  });
}

async function backendRespondeu(): Promise<boolean> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 3000);
  try {
    const r = await fetch(`${ORIGEM_API}/health`, { cache: 'no-store', signal: ctrl.signal });
    return r.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

// Momento 4 — o front novo está publicado. É o aviso de sempre.
export function promptVersionUpdate(buildId?: string) {
  if (buildId) versaoDisponivel = buildId;
  if (Date.now() < dispensadoAte) return;
  if (fase === 'nova_versao') {
    // Pode chegar uma versão mais nova enquanto o aviso continua aberto.
    linkAtualizar?.setAttribute('href', urlParaAtualizar());
    return;
  }
  if (tique) { clearInterval(tique); tique = null; }
  fase = 'nova_versao';
  pintar('Nova versão disponível', 'Atualize quando terminar seu atendimento.', true);
}

// Momentos 1 a 3 — o deploy avisou que vai reiniciar.
export function avisarDeployEmBreve(reinicio: number, expira: number) {
  if (fase === 'nova_versao') return; // já há coisa melhor na tela
  if (Date.now() < dispensadoAte) return;
  reinicioEm = reinicio;
  expiraEm = expira;
  if (tique) return; // já estamos contando
  fase = 'contagem';
  const passo = async () => {
    const agora = Date.now();
    // O deploy abortou (ou era só um teste) e o backend nunca caiu: sai de cena
    // em vez de ficar "Atualizando..." para sempre.
    if (agora > expiraEm) { fechar(); return; }
    if (agora < reinicioEm) {
      const s = Math.max(1, Math.ceil((reinicioEm - agora) / 1000));
      fase = 'contagem';
      pintar('Atualização do CRM', `Começa em <strong style="font-weight:500">${s}s</strong>. Termine o que está escrevendo.`, false);
      return;
    }
    if (fase !== 'voltou') {
      if (fase !== 'atualizando') {
        fase = 'atualizando';
        pintar('Atualizando o CRM', 'Alguns segundos. Não é erro — nada foi perdido.', false);
      }
      if (await backendRespondeu()) {
        // Verde é só desfecho, e voltar do ar é desfecho.
        fase = 'voltou';
        pintar('Pronto — o CRM voltou', 'Se a tela nova chegar, o aviso volta aqui.', false, 'hsl(var(--success-text))');
        setTimeout(() => { if (fase === 'voltou') fechar(); }, 4000);
      }
    }
  };
  void passo();
  tique = setInterval(() => { void passo(); }, 1000);
}
